'use client';

import { useEffect } from 'react';

// Exact cache identities from our retired /sw.js, including its former brand.
// A shared origin may host unrelated workers/caches; never enumerate-and-delete.
export const LEGACY_BROWSER_CACHE_NAMES = new Set([
  'octolab-v1', 'octolab-runtime', 'hoppingrabbit-v1', 'hoppingrabbit-runtime',
]);

export interface LegacyCleanupFailure {
  action: 'list_workers' | 'unregister_worker' | 'list_caches' | 'delete_cache';
  errorName: string;
}

export async function cleanupLegacyBrowserResources(): Promise<LegacyCleanupFailure[]> {
  const failures: LegacyCleanupFailure[] = [];
  const attempt = async (action: LegacyCleanupFailure['action'], run: () => Promise<unknown>) => {
    try { return await run(); }
    catch (error) {
      failures.push({action, errorName: error instanceof Error ? error.name : 'UnknownError'});
      return undefined;
    }
  };
  await Promise.all([
    attempt('list_workers', async () => {
      if (!('serviceWorker' in navigator)) return;
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(async (registration) => {
        const scope = new URL(registration.scope);
        const workers = [registration.active, registration.waiting, registration.installing].filter(Boolean);
        if (scope.origin !== window.location.origin || scope.pathname !== '/' || !workers.length) return;
        const owned = workers.every((worker) => {
          const script = new URL(worker!.scriptURL);
          return script.origin === window.location.origin && script.pathname === '/sw.js';
        });
        if (owned) await attempt('unregister_worker', () => registration.unregister());
      }));
    }),
    attempt('list_caches', async () => {
      if (!('caches' in window)) return;
      const storage = window.caches;
      const names = await storage.keys();
      await Promise.all(names.filter((name) => LEGACY_BROWSER_CACHE_NAMES.has(name))
        .map((name) => attempt('delete_cache', () => storage.delete(name))));
    }),
  ]);
  return failures;
}

export function ServiceWorkerRegister() {
  useEffect(() => {
    // Optional cleanup must settle every rejection; blocked storage must not
    // turn an otherwise working page into an unhandled runtime exception.
    void cleanupLegacyBrowserResources().then((failures) => {
      if (failures.length) console.warn('[LegacyBrowserCleanup]', failures);
    }).catch(() => { /* Browser API/reporting access can itself be denied. */ });
  }, []);

  return null;
}
